import React, { useState, useRef } from 'react';
import httpService from "../../services/httpService";
import Card from 'react-bootstrap/Card';
import Collapse from 'react-bootstrap/Collapse';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { FaChevronDown, FaChevronRight, FaPaperPlane, FaListUl, FaUser } from 'react-icons/fa';
import UserInfo from './UserInfo';
import '../../styles/EpisodeUserItem.scss';

const EpisodeUserItem = ({ userGuid, episodeGuid, clubGuid, userSummary, isExpanded, onToggle, elementIndex }) => {
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [expandedComments, setExpandedComments] = useState({});
    const [newComments, setNewComments] = useState({});
    const [submittingComment, setSubmittingComment] = useState({});
    const commentsContainerRefs = useRef({});
    const initializedPractices = useRef(new Set());

    const toggleUser = async () => {
        if (!isExpanded) {
            // Expanding - load fresh data first
            setLoading(true);
            try {
                const response = await httpService.get(`/dash/episode/${episodeGuid}/user/${userGuid}`);
                setUserData(response.data.user);
                // Only expand after data is loaded - notify parent
                onToggle();
            } catch (err) {
                console.error('Error fetching user practices:', err);
                alert(err.response?.data?.message || 'Failed to load user data');
            } finally {
                setLoading(false);
            }
        } else {
            // Collapsing - notify parent then clear data after animation
            onToggle();
            setTimeout(() => {
                setUserData(null);
                setExpandedComments({});
            }, 1000);
        }
    };

    const toggleComments = (practiceId) => {
        setExpandedComments(prev => ({
            ...prev,
            [practiceId]: !prev[practiceId]
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCommentTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleCommentChange = (practiceId, value) => {
        setNewComments(prev => ({
            ...prev,
            [practiceId]: value
        }));
    };

    const scrollToBottom = (practiceId) => {
        setTimeout(() => {
            const container = commentsContainerRefs.current[practiceId];
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 100);
    };

    const submitComment = async (practiceId) => {
        const commentText = newComments[practiceId];
        if (!commentText || !commentText.trim()) return;

        setSubmittingComment(prev => ({ ...prev, [practiceId]: true }));

        try {
            const response = await httpService.post(`/comments/${practiceId}`, {
                body: commentText.trim()
            });

            // Update the practice comments in the userData state
            setUserData(prevData => {
                return {
                    ...prevData,
                    practices: prevData.practices.map(practice => {
                        if (practice.id === practiceId) {
                            return {
                                ...practice,
                                comments: [...(practice.comments || []), response.data.comment]
                            };
                        }
                        return practice;
                    })
                };
            });

            // Clear the input
            setNewComments(prev => ({
                ...prev,
                [practiceId]: ''
            }));

            // Scroll to bottom
            scrollToBottom(practiceId);
        } catch (err) {
            console.error('Error submitting comment:', err);
            alert(err.response?.data?.message || 'Failed to submit comment');
        } finally {
            setSubmittingComment(prev => ({ ...prev, [practiceId]: false }));
        }
    };

    const renderComments = (comments, practiceId) => {
        if (!comments || comments.length === 0) {
            return <p className="text-muted small text-center">No comments yet</p>;
        }

        return (
            <div
                ref={(el) => {
                    commentsContainerRefs.current[practiceId] = el;
                    if (el && !initializedPractices.current.has(practiceId)) {
                        // First time mounting - scroll to bottom after Collapse animation
                        initializedPractices.current.add(practiceId);
                        setTimeout(() => {
                            el.scrollTop = el.scrollHeight;
                        }, 400);
                    }
                }}
                className="episode-user-comments-container"
            >
                {comments.map((comment) => {
                    const isPodcaster = comment.is_podcaster;
                    return (
                        <div
                            key={comment.id}
                            className={`my-1 d-flex mx-1 ${isPodcaster ? 'justify-content-start' : 'justify-content-end'}`}
                        >
                            <div className={`episode-user-comment-bubble ${isPodcaster ? 'podcaster' : 'listener'}`}>
                                <div className="d-flex align-items-center mb-1">
                                    <strong className="small">
                                        {comment.user.first_name} {comment.user.last_name}
                                    </strong>
                                    {!isPodcaster && (
                                        <Badge bg="primary" className="ms-2 episode-user-badge-small">
                                            Listener
                                        </Badge>
                                    )}
                                </div>
                                <p className="mb-1 episode-user-comment-body">{comment.body}</p>
                                <small className="text-muted episode-user-comment-time">
                                    {formatCommentTime(comment.created_at)}
                                </small>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const completionRate = userSummary.stats.total_practices > 0
        ? Math.round((userSummary.stats.completed_practices / userSummary.stats.total_practices) * 100)
        : 0;

    return (
        <Card className={"mb-0 fake-accordion index-"+elementIndex}>
            <Card.Header
                onClick={toggleUser}
                className="episode-user-header-dddd d-flex justify-content-between align-items-center border-0"
            >
                <div className="d-flex align-items-center flex-grow-1">
                    <div className="me-2 episode-user-icon">
                        {loading ? (
                            <Spinner animation="border" size="sm" />
                        ) : isExpanded ? (
                            <FaChevronDown />
                        ) : (
                            <FaChevronRight />
                        )}
                    </div>
                    <div className="me-3">
                        <strong>{userSummary.user.first_name} {userSummary.user.last_name}</strong>
                        <div className="text-muted small">{userSummary.user.email}</div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">{userSummary.stats.total_practices} total</Badge>
                    <Badge bg="success">{userSummary.stats.completed_practices} completed</Badge>
                    <Badge bg="info">{userSummary.stats.shared_for_review} shared</Badge>
                    <div className="d-flex align-items-center ms-2">
                        <div className="progress episode-user-progress">
                            <div
                                className={`progress-bar ${completionRate >= 50 ? 'bg-success' : 'bg-warning'}`}
                                role="progressbar"
                                style={{ width: `${completionRate}%` }}
                                aria-valuenow={completionRate}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                        <span className="small">{completionRate}%</span>
                    </div>
                </div>
            </Card.Header>
            <Collapse className={'border-0'} in={isExpanded}>
                <div className={'border-0'}>
                    <Card.Body className={'border-0'}>
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status" size="sm">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-2 small text-muted">Loading practices...</p>
                            </div>
                        ) : userData ? (
                            <Tabs defaultActiveKey="practices" id="user-tabs" className="mb-3">
                                <Tab eventKey="practices" title={<span><FaListUl className="me-1" />Practices for this Episode</span>}>
                                    {userData.practices && userData.practices.length > 0 ? (
                                        <div className="list-group">
                                    {userData.practices.map((practice) => (
                                        <div key={practice.id} className="list-group-item">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <h5 className="mb-0 me-2">{practice.title}</h5>
                                                        <Badge bg={practice.type === 'custom' ? 'indigo' : 'info'}>
                                                            {practice.type}
                                                        </Badge>
                                                        {practice.completed && (
                                                            <Badge bg="success" className="ms-2">Completed</Badge>
                                                        )}
                                                    </div>
                                                    <p className="mb-2 text-muted small">{practice.body}</p>
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">
                                                    <strong>Practice Date:</strong> {formatDate(practice.practice_date_time)}
                                                    {practice.completion_date_time && (
                                                        <> | <strong>Completed:</strong> {formatDate(practice.completion_date_time)}</>
                                                    )}
                                                    {practice.shared_for_review && (
                                                        <> | <strong>Shared for review:</strong> {formatDate(practice.shared_for_review)}</>
                                                    )}
                                                </small>
                                            </div>
                                            {practice.skills && practice.skills.length > 0 && (
                                                <div className="mb-2">
                                                    <small className="text-muted"><strong>Skills:</strong></small> &nbsp;
                                                    {practice.skills.map(skill => (
                                                        <Badge key={skill.id} bg="secondary" className="me-1 mb-1">
                                                            {skill.title}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Comments Section */}
                                            <div className="mt-3 border-top pt-3">
                                                <div
                                                    onClick={() => toggleComments(practice.id)}
                                                    className="episode-user-comments-toggle d-flex align-items-center mb-2"
                                                >
                                                    <div className="me-2">
                                                        {expandedComments[practice.id] ? <FaChevronDown /> : <FaChevronRight />}
                                                    </div>
                                                    <h6 className="mb-0">
                                                        Comments
                                                        {practice.comments && practice.comments.length > 0 && (
                                                            <>
                                                                <Badge bg="light" className="ms-2">{practice.comments.length}</Badge>
                                                                <Badge
                                                                    bg={practice.comments[practice.comments.length - 1].is_podcaster ? "success" : "primary"}
                                                                    className="ms-2 episode-user-badge-small"
                                                                >
                                                                    Last comment by {practice.comments[practice.comments.length - 1].is_podcaster ? "You" : "Listener"}
                                                                </Badge>
                                                            </>
                                                        )}
                                                    </h6>
                                                </div>

                                                <Collapse in={expandedComments[practice.id]}>
                                                    <div>
                                                        {/* Display Comments */}
                                                        {renderComments(practice.comments, practice.id)}

                                                        {/* Add Comment Input */}
                                                        <div className="mt-3">
                                                            <InputGroup>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={2}
                                                                    placeholder="Write a comment..."
                                                                    value={newComments[practice.id] || ''}
                                                                    onChange={(e) => handleCommentChange(practice.id, e.target.value)}
                                                                    disabled={submittingComment[practice.id]}
                                                                />
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => submitComment(practice.id)}
                                                                    disabled={!newComments[practice.id]?.trim() || submittingComment[practice.id]}
                                                                >
                                                                    {submittingComment[practice.id] ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        <FaPaperPlane />
                                                                    )}
                                                                </Button>
                                                            </InputGroup>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </div>
                                        </div>
                                    ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No practices found for this user.</p>
                                    )}
                                </Tab>
                                <Tab eventKey="userinfo" title={<span><FaUser className="me-1" />Listener Info</span>}>
                                    <UserInfo
                                        userGuid={userGuid}
                                        clubGuid={clubGuid}
                                        context={{
                                            type: 'episode',
                                            data: {
                                                resource: 'episode',
                                                guid: episodeGuid
                                            }
                                        }}
                                    />
                                </Tab>
                            </Tabs>
                        ) : (
                            <p className="text-muted">No data available.</p>
                        )}
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};

export default EpisodeUserItem;